// GET /api/rules and GET /api/rules/{id}/matches (design §s08b, FEC-M4-03).
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import client from '../client';
import { parseEnvelope } from '../envelope';

export const ruleRowSchema = z.object({
  rule_id: z.string().uuid(),
  kind: z.string(),
  restatement: z.string(),
  enabled: z.boolean(),
  effective_from: z.string().nullable(),
  effective_to: z.string().nullable(),
  priority: z.number(),
  match_count: z.number(),
  matched_at: z.string().nullable(),
  version: z.number().int().positive().default(1),
  cache_state: z.enum(['current', 'rebuilding', 'failed']).default('current'),
  known_match_count: z.number().int().nonnegative().nullable().default(null),
});
export type RuleRow = z.infer<typeof ruleRowSchema>;

export const ruleMatchSchema = z.object({
  transaction_id: z.string().uuid(),
  posted_at: z.string(),
  merchant_display: z.string().nullable(),
  amount: z.string(),
});
export type RuleMatch = z.infer<typeof ruleMatchSchema>;

export const rulesQueryKey = ['rules'] as const;

export function useRules(enabled = true) {
  return useQuery<RuleRow[]>({
    queryKey: rulesQueryKey,
    enabled,
    queryFn: async () => {
      const response = await client.get('/api/rules');
      return parseEnvelope(z.array(ruleRowSchema), response.data).data;
    },
  });
}

export function useRuleMatches(ruleId: string | null) {
  return useQuery<RuleMatch[]>({
    queryKey: ['rules', ruleId, 'matches'],
    enabled: Boolean(ruleId),
    queryFn: async () => {
      const response = await client.get(`/api/rules/${ruleId}/matches`);
      return parseEnvelope(z.array(ruleMatchSchema), response.data).data;
    },
  });
}
